import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Catalogo from "./pages/Catalogo";
import CriarCodigo from "./pages/CriarCodigo";
import Usuarios from "./pages/Usuarios";
import Importar from "./pages/Importar";
import Historico from "./pages/Historico";
import Reservas from "./pages/Reservas";
import Desenhos from "./pages/Desenhos";
import Estruturas from "./pages/Estruturas";
import Admin from "./pages/Admin";
import Kiosk from "./pages/Kiosk";
import SDP from "./pages/SDP";
import Reunioes from "./pages/Reunioes";
import SCP from "./pages/SCP";
import Catalogos from "./pages/Catalogos";
import VerifyEmail from "./pages/VerifyEmail";

import "./App.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-slate-500">Carregando...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-slate-500">Carregando...</p>
        </div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Seed data on first load
const DataSeeder = ({ children }) => {
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API_URL}/api/seed`);
      } catch (error) {
        // Ignore errors, seed is not critical
      }
    };
    seedData();
  }, []);
  
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DataSeeder>
          <Routes>
            <Route 
              path="/" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/catalogo" 
              element={
                <ProtectedRoute>
                  <Catalogo />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/criar" 
              element={
                <ProtectedRoute>
                  <CriarCodigo />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/usuarios" 
              element={
                <ProtectedRoute>
                  <Usuarios />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/importar" 
              element={
                <ProtectedRoute>
                  <Importar />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/historico" 
              element={
                <ProtectedRoute>
                  <Historico />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/reservas" 
              element={
                <ProtectedRoute>
                  <Reservas />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/desenhos" 
              element={
                <ProtectedRoute>
                  <Desenhos />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/estruturas" 
              element={
                <ProtectedRoute>
                  <Estruturas />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/sdp" 
              element={
                <ProtectedRoute>
                  <SDP />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/reunioes" 
              element={
                <ProtectedRoute>
                  <Reunioes />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/scp" 
              element={
                <ProtectedRoute>
                  <SCP />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/catalogos" 
              element={
                <ProtectedRoute>
                  <Catalogos />
                </ProtectedRoute>
              } 
            />
            {/* Kiosk - Public route with token-based auth */}
            <Route path="/kiosk" element={<Kiosk />} />
            {/* Email verification - Public route */}
            <Route path="/verificar-email" element={<VerifyEmail />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </DataSeeder>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
